import CollectionList       from '@/js/Shared/Collections/CollectionList';
import PropertiesValidation from '@/js/Shared/Validation/PropertiesValidation';
import ContactPersonItem    from '../Entities/ContactPersonItem';

export {ContactsBoxesCollection as default};

/**
* Contacts Boxes Collectiin
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsBoxesCollection extends CollectionList {
  /**
   * Full contact list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {ContactsCollectionLists}
   */
  contacts;

  /**
   * Keeps track of all emails added to avoid duplicates
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  emails = {};

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  constructor() {
    super('id');
  }

 /**
   * Sets contacts collection data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactsCollectionLists} contacts Contacts collection lists
   *
   * @return {void}
   */
  setContacts(contacts) {
    this.contacts = contacts;
  }

 /**
   * Adds a contact to the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} contact
   *
   * @return {Boolean}
   */
  add(item) {
    if (this.emails[item.email]) {
      return false;
    }

    if (item.email) {
      this.emails[item.email] = true;
    }

    super.add(item);
    return true;
  }

 /**
   * Deletes a contact from the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} contact
   *
   * @return {Boolean}
   */
  delete(id) {
    if (super.delete(id) === false) {
      return false;
    }

    if (this.last_deleted.email) {
      delete this.emails[this.last_deleted.email];
    }
  }

  /**
   * Returns all contacts emails in the list as a string
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  getEmailsAsString() {
    let emails = [];

    this.items.forEach(item => {
      if (item.email){
        emails.push(item.email);
      }
    });

    return emails.join(',');
  }

  /**
   * Get a contact from the list using the email as a reference
   *
   * @param {String} email
   *
   * @returns {Object.<ContactPersonItem>|null}
   */
  getPersonByEmail(email){
    let person = this.items.find(item => item.email == email);

    return person;
  }

  /**
   * Returns a list of all contact recipients in
   * the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Array}
   */
  getRecipients() {
    let recipients = [];

    this.items.forEach(item => {
      recipients.push(item.recipient);
    });

    return recipients;
  }

   /**
   * Deletes all contacts in the collection that have an email in the given list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array} emails emails list
   *
   * @return {void}
   */
  deletePersonsByEmail(emails) {
    emails.forEach((email) => {
      this.items.forEach((contact) => {
        if (contact.email === email) {
          this.delete(contact.id);
          return;
        }
      })
    })
  }

   /**
   * Adds a list of person entities by an array of items
   *
   * Applies a set of regex rules to the input data to split and convert the input data into contact entities
   *
   * If a person is not found creates a new entity with the input data assigned to the corresponding field
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array} input input data
   *
   * @return {void}
   */
   addPersonsByArray(input) {
    input.forEach((element) => {
      this.addPersonByType(element)
    });
  }

  /**
   * Adds a contact list of person entities by an arbitrary string
   *
   * Applies a set of regex rules to the input data to split and convert the input data into contact entities
   *
   * If a person is not found creates a new entity with the input data assigned to the corresponding field
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} input input data
   *
   * @return {void}
   */
  addPersonsByString(input) {
    let input_list = input.split(/[;,\s]+/).filter(element => element);

    let values = '';

    input_list.forEach((element) => {
      if (element.match(PropertiesValidation.REGEX_RULES['email'])) {
        this.addPersonByEmail(element);

        if (values) {
          this.addPersonByName(values);
          values = '';
        }
        return;
      }

      values += element + ' ';
    });

    if (values) {
      this.addPersonByName(values);
    }
  }

  /**
   * Adds a contact person entity by an arbitrary value
   *
   * Applies a set of regex rules to the input data to figure out which field to search
   *
   * If a person is not found creates a new entity with the input data assigned to the corresponding field
   *
   * Add the first exact match found
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} value input value
   *
   * @return {void}
   */
  addPersonByType(value) {
    value = value.trim();

    (value.match(PropertiesValidation.REGEX_RULES['email']))
      ? this.addPersonByEmail(value)
      : this.addPersonByName(value);
  }

  /**
   * Add a contact person by name
   *
   * If a person is not found creates a new entity with the input data assigned to the name field
   *
   * Adds the first exact match found
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} name
   *
   * @return {void}
   */
  addPersonByName(name) {
    let email = null;

    if (name.includes('<') && name.includes('>')) {
      email = name.substring(name.lastIndexOf('<') + 1, name.lastIndexOf('>')).trim();
      name  = name.substring(0, name.lastIndexOf('<')).trim();
    }

    let name_lower = name.toLowerCase().latinize().replace(/\s/g, '');

    let person = this.contacts.persons.items.find((contact) => {
      return (contact.name && contact.name.toLowerCase().latinize().replace(/\s/g, '') === name_lower) ? true : false
    });

    if (!person) {
      let recipient = (email && email.match(PropertiesValidation.REGEX_RULES['email'])) ? email : null;

      person           = new ContactPersonItem();
      person.id        = Math.random().toString(18);
      person.name      = name;
      person.email     = recipient;
      person.recipient = recipient;
      person.temporary = true;
      person.recipient = '"' + person.name + '"';
    }

    this.add(person);
  }

  /**
   * Adds a contact person entity by email
   *
   * If a person is not found creates a new entity with the input data assigned to the email field
   *
   * Adds the first exact match found
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {string} input search input
   *
   * @return {Boolean}
   */
  addPersonByEmail(input, ) {
    if (!input.match(PropertiesValidation.REGEX_RULES['email'])) {
      return false;
    }

    input      = input.toLowerCase().latinize().trim() || '';
    let person = this.contacts.persons.items.find((contact) => {
      return (contact.email && contact.email.toLowerCase() === input) ? true : false
    });

    if (!person) {
      person           = new ContactPersonItem();
      person.id        = input;
      person.name      = input,
      person.email     = input,
      person.recipient = person.email;
      person.temporary = true;
    }

    this.add(person);
    return true;
  }

  /**
   * Checks if all contacts in the list have an email set
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  validEmails() {
    let valid = true;

    this.items.every((item) => {
      if (!item.email) {
        valid = false;
        return false;
      }
    });

    return valid;
  }

  /**
   * Resests the collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.emails = {};
    super.reset();
  }
}